import React, { ReactNode, useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useHistory, useLocation } from "react-router";
import { useTranslation } from "react-i18next";
import useRank5Auth from "../../shared/useRank5Auth";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import {
  TextField,
  Button,
  Paper,
  Grid,
  Box,
  Stack,
  Typography,
  InputAdornment,
  useMediaQuery,
  Chip,
  Skeleton,
  ToggleButtonGroup,
  ToggleButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Drawer,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from "@mui/material";
import Deck from "../../shared/api/DeckApi";
import { CardDeckPreview, CreatorStats, DeckStat, Role } from "../../shared/interfaces/GameLogicTypes";
import CardDeck from "../../components/CardDeck/CardDeck";
import {
  convertBackendToFrontendDecks,
} from "../../shared/helper/functions";
import _ from "underscore";
import CardWarning from "../../components/CardWarning/CardWarning";
import CircularProgress from "@mui/material/CircularProgress";
import ClearIcon from "@mui/icons-material/Clear";
import { useTheme } from "@emotion/react";
import { Theme as MuiTheme } from "@mui/material/styles";
import { keyframes } from "@mui/system";
import { sortDecks } from "./ProfilePageLogic";
import DoNotDisturbAltIcon from "@mui/icons-material/DoNotDisturbAlt";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import TranslateIcon from "@mui/icons-material/Translate";
import DashboardIcon from "@mui/icons-material/Dashboard";
import StyleIcon from "@mui/icons-material/Style";
import SettingsIcon from "@mui/icons-material/Settings";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import MenuIcon from "@mui/icons-material/Menu";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import LinkIcon from "@mui/icons-material/Link";
import { CreateDeckConfigMenu } from "../../components/CreateDeckConfigMenu/CreateDeckConfigMenu";
import { Languages } from "../../shared/language/languages";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

declare module "@emotion/react" {
  export interface Theme extends MuiTheme {}
}

const GridItem = (props: { children: ReactNode }) => {
  return (
    <Grid item xs={1}>
      {props.children}
    </Grid>
  );
};

// ─── Name Status ─────────────────────────────────────────────────────────────
type NameStatus = "idle" | "checking" | "saved" | "taken" | "saving";
type ProfileView = "dashboard" | "decks" | "settings";
type DeckSort = "recent" | "most_played" | "alphabetical";

const SIDEBAR_WIDTH = 250;

export default function ProfilePage() {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [returnedName, setReturnedName] = useState("");
  const [role, setRole] = useState(Role.CREATOR);
  const [decks, setDecks] = useState<Array<CardDeckPreview>>([]);
  const [stagedDeletion, setStagedDeletion] = useState<() => void>(() => {});
  const [showDeleteDeckWarning, setShowDeleteDeckWarning] = useState(false);
  const [dontShowDeleteDeckWarningAgain, setDontShowDeleteDeckWarningAgain] = useState(false);
  const inputEl = useRef<HTMLInputElement>(null);
  const history = useHistory();
  const location = useLocation();

  // Stats state
  const [stats, setStats] = useState<CreatorStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [statsDays, setStatsDays] = useState<number>(30);

  // Name auto-save state
  const [nameStatus, setNameStatus] = useState<NameStatus>("idle");
  const nameTimerRef = useRef<NodeJS.Timeout | null>(null);
  const savedTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Sidebar & view state
  const [activeView, setActiveView] = useState<ProfileView>("dashboard");
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const view = params.get("view") as ProfileView | null;
    if (view && (["dashboard", "decks", "settings"] as ProfileView[]).includes(view)) {
      setActiveView(view);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Decks view state
  const [deckSearch, setDeckSearch] = useState("");
  const [deckSort, setDeckSort] = useState<DeckSort>("recent");

  // Bio state
  const [bio, setBio] = useState("");
  const bioTimerRef = useRef<NodeJS.Timeout | null>(null);
  const [bioSaved, setBioSaved] = useState(false);

  // Language rank cycling state
  const [langRankIndex, setLangRankIndex] = useState(0);

  // Share link state
  const [configMenuAnchor, setConfigMenuAnchor] = useState<HTMLButtonElement | null>(null);
  const [shareLinkDialogOpen, setShareLinkDialogOpen] = useState(false);
  const [shareLinkSelectedIds, setShareLinkSelectedIds] = useState<string[]>([]);

  let theme = useTheme();
  let isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const {
    getAccessTokenSilently,
    logout,
  } = useRank5Auth();

  // ─── Name auto-save logic ───────────────────────────────────────────────────
  const handleNameChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newName = event.target.value;
      setName(newName);

      // Clear previous timers
      if (nameTimerRef.current) clearTimeout(nameTimerRef.current);
      if (savedTimerRef.current) clearTimeout(savedTimerRef.current);

      if (newName.trim().length === 0 || newName === returnedName) {
        setNameStatus("idle");
        return;
      }

      setNameStatus("checking");

      nameTimerRef.current = setTimeout(async () => {
        try {
          const token = await getAccessTokenSilently();
          const isTaken = await Deck.checkUserName(token, newName);
          if (isTaken) {
            setNameStatus("taken");
          } else {
            // Auto-submit
            setNameStatus("saving");
            const savedName = await Deck.changeUserName(token, newName);
            if (savedName) {
              setReturnedName(savedName);
              setName(savedName);
              setNameStatus("saved");
              savedTimerRef.current = setTimeout(() => setNameStatus("idle"), 1500);
            } else {
              setNameStatus("taken");
            }
          }
        } catch (_) {
          setNameStatus("idle");
        }
      }, 500);
    },
    [returnedName, getAccessTokenSilently]
  );

  const handleBioChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const newBio = event.target.value;
      setBio(newBio);
      setBioSaved(false);
      if (bioTimerRef.current) clearTimeout(bioTimerRef.current);
      bioTimerRef.current = setTimeout(async () => {
        try {
          const token = await getAccessTokenSilently();
          await Deck.changeUserBio(token, newBio);
          setBioSaved(true);
          setTimeout(() => setBioSaved(false), 1500);
        } catch (_) {}
      }, 800);
    },
    [getAccessTokenSilently]
  );

  const handleRevertName = () => {
    setName(returnedName);
    setNameStatus("idle");
  };

  // ─── Data loading ──────────────────────────────────────────────────────────
  useEffect(() => {
    const loadData = async () => {
      try {
        const token = await getAccessTokenSilently();
        const [profileRes, statsRes] = await Promise.all([
          Deck.loadOwnProfile(token),
          Deck.getCreatorStats(token, statsDays),
        ]);

        const profileInfo = profileRes.profileInfo;
        setName(profileInfo.name);
        setReturnedName(profileInfo.name);
        setRole(profileInfo.role);
        setBio(profileInfo.bio ?? "");
        setDecks(convertBackendToFrontendDecks(profileInfo.decks));

        if (statsRes) {
          setStats(statsRes);
        }
      } catch (e) {
        console.error("Failed to load profile data", e);
      } finally {
        setStatsLoading(false);
      }
    };
    loadData();
  }, []);

  // Reload stats when days toggle changes
  useEffect(() => {
    (async () => {
      try {
        setStatsLoading(true);
        const token = await getAccessTokenSilently();
        const statsRes = await Deck.getCreatorStats(token, statsDays);
        if (statsRes) setStats(statsRes);
      } catch (_) {
      } finally {
        setStatsLoading(false);
      }
    })();
  }, [statsDays]);

  // ─── Deck operations ──────────────────────────────────────────────────────
  const handleDelete = async (deckId: string) => {
    const stagedDeletion = async () => {
      let token = await getAccessTokenSilently();
      let success = await Deck.deleteDeck(token, deckId);
      if (success) {
        let index = _.findIndex(decks, (deck: CardDeckPreview) => deck.id === deckId);
        if (index !== -1) {
          let newDeckStack = [...decks];
          newDeckStack.splice(index, 1);
          setDecks(newDeckStack);
        }
      }
    };
    if (dontShowDeleteDeckWarningAgain) {
      stagedDeletion();
    } else {
      setStagedDeletion(() => stagedDeletion);
      setShowDeleteDeckWarning(true);
    }
  };

  const handleCreateNewDeck = async () => {
    let token = await getAccessTokenSilently();
    let deckId = await Deck.createDeck(token, "en");
    if (deckId) {
      history.push(`/deck/${deckId}`);
    }
  };

  useEffect(() => {
    if (!showDeleteDeckWarning) setStagedDeletion(() => {});
  }, [showDeleteDeckWarning]);

  // ─── Animations ────────────────────────────────────────────────────────────
  const fadeIn = keyframes`
    0% { opacity: 0 }
    100% { opacity: 1 }
  `;

  const greenFlash = keyframes`
    0% { border-color: #4caf50 }
    100% { border-color: transparent }
  `;

  // ─── Name field color ─────────────────────────────────────────────────────
  const getNameFieldColor = (): "primary" | "success" | "error" => {
    if (nameStatus === "saved") return "success";
    if (nameStatus === "taken") return "error";
    return "primary";
  };

  // ─── Recharts type aliases ─────────────────────────────────────────────────
  const AreaChartAny = AreaChart as any;
  const AreaAny = Area as any;
  const XAxisAny = XAxis as any;
  const YAxisAny = YAxis as any;
  const CartesianGridAny = CartesianGrid as any;
  const TooltipAny = Tooltip as any;
  const ResponsiveContainerAny = ResponsiveContainer as any;

  // ─── Filtered & sorted decks ───────────────────────────────────────────────
  const filteredDecks = useMemo(() => {
    let result = [...decks];

    if (deckSearch.trim()) {
      const query = deckSearch.toLowerCase();
      result = result.filter((d) => d.name.toLowerCase().includes(query));
    }

    // Get play counts from stats for sorting
    const playCountMap = new Map<string, number>();
    if (stats?.deckStats) {
      for (const ds of stats.deckStats) {
        playCountMap.set(ds.deckId, ds.playCount);
      }
    }

    switch (deckSort) {
      case "most_played":
        result.sort((a, b) => (playCountMap.get(b.id) ?? 0) - (playCountMap.get(a.id) ?? 0));
        break;
      case "alphabetical":
        result.sort((a, b) => a.name.localeCompare(b.name));
        break;
      case "recent":
      default:
        result.sort(sortDecks);
        break;
    }

    return result;
  }, [decks, deckSearch, deckSort, stats]);

  // ─── Sidebar name display (compact) ────────────────────────────────────────
  const sidebarNameField = () => (
    <Stack spacing={0.5}>
      <TextField
        variant="outlined"
        size="small"
        color={getNameFieldColor()}
        inputProps={{
          style: { textAlign: "center", fontSize: 16 },
          maxLength: 18,
        }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              {nameStatus === "checking" || nameStatus === "saving" ? (
                <CircularProgress size={16} />
              ) : nameStatus === "saved" ? (
                <CheckCircleOutlineIcon color="success" sx={{ fontSize: 18 }} />
              ) : nameStatus === "taken" ? (
                <ClearIcon
                  sx={{ cursor: "pointer", color: "error.main", fontSize: 18 }}
                  onClick={handleRevertName}
                />
              ) : null}
            </InputAdornment>
          ),
        }}
        sx={{
          ...(nameStatus === "saved" && {
            "& .MuiOutlinedInput-notchedOutline": {
              animation: `${greenFlash} 1.5s ease-out`,
            },
          }),
        }}
        value={name}
        onChange={handleNameChange}
        helperText={nameStatus === "taken" ? t("name_taken") : undefined}
      />
    </Stack>
  );

  // ─── Navigation items ────────────────────────────────────────────────────
  const navItems: { view: ProfileView; icon: React.ReactNode; label: string }[] = [
    { view: "dashboard", icon: <DashboardIcon />, label: t("profile_dashboard") },
    { view: "decks", icon: <StyleIcon />, label: t("profile_my_decks") },
    { view: "settings", icon: <SettingsIcon />, label: t("profile_settings") },
  ];

  const handleNavClick = (view: ProfileView) => {
    setActiveView(view);
    if (isMobile) setDrawerOpen(false);
  };

  // ─── Sidebar content ────────────────────────────────────────────────────────
  const sidebarContent = () => (
    <Stack
      sx={{
        height: "100%",
        p: 2,
        justifyContent: "space-between",
      }}
    >
      <Stack spacing={2}>
        {sidebarNameField()}
        <List disablePadding>
          {navItems.map((item) => (
            <ListItemButton
              key={item.view}
              selected={activeView === item.view}
              onClick={() => handleNavClick(item.view)}
              sx={{ borderRadius: 1, mb: 0.5 }}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>{item.icon}</ListItemIcon>
              <ListItemText primary={item.label} />
            </ListItemButton>
          ))}
        </List>
      </Stack>
      <Stack spacing={1}>
        <Button
          variant="outlined"
          startIcon={<ExitToAppIcon />}
          onClick={() => history.push("")}
          fullWidth
        >
          Exit
        </Button>
        <Button
          variant="outlined"
          color="error"
          onClick={() => logout()}
          fullWidth
          size="small"
        >
          Log out
        </Button>
      </Stack>
    </Stack>
  );

  // ─── RankCard sub-component ─────────────────────────────────────────────────
  const RankCard = ({ icon, label, rank, total, onClick }: { icon: React.ReactNode; label: string; rank: number; total: number; onClick?: () => void }) => (
    <Paper sx={{ p: 2, textAlign: "center", minWidth: 120, flex: 1, ...(onClick && { cursor: "pointer" }) }} onClick={onClick}>
      <Stack alignItems="center" spacing={0.5}>
        {icon}
        <Typography variant="caption" color="text.secondary">{label}</Typography>
        <Typography variant="h5" fontWeight="bold">
          {rank > 0 ? `#${rank}` : "\u2014"}
        </Typography>
        {total > 0 && (
          <Typography variant="caption" color="text.secondary">
            {t("out_of_creators", { rank, total })}
          </Typography>
        )}
      </Stack>
    </Paper>
  );

  // ─── Dashboard View ─────────────────────────────────────────────────────────
  const dashboardView = () => {
    if (statsLoading) {
      return (
        <Stack spacing={2}>
          <Stack direction="row" spacing={2}>
            <Skeleton variant="rectangular" sx={{ borderRadius: 1, flex: 1, height: 100 }} />
            <Skeleton variant="rectangular" sx={{ borderRadius: 1, flex: 1, height: 100 }} />
          </Stack>
          <Skeleton variant="rectangular" sx={{ borderRadius: 1, height: 200 }} />
          <Skeleton variant="rectangular" sx={{ borderRadius: 1, height: 150 }} />
        </Stack>
      );
    }

    if (!stats || (stats.totalPlays === 0 && stats.globalRank === 0)) {
      return (
        <Paper sx={{ p: 4, textAlign: "center" }}>
          <Typography color="text.secondary" sx={{ mb: 2 }}>{t("no_play_data")}</Typography>
          {decks.length > 0 && (
            <Button
              variant="contained"
              startIcon={<LinkIcon />}
              onClick={() => {
                setShareLinkSelectedIds(decks.map((d) => d.id));
                setShareLinkDialogOpen(true);
              }}
            >
              Create Share Link
            </Button>
          )}
        </Paper>
      );
    }

    const chartData = stats.dailyPlays;
    const topDecks = [...(stats.deckStats || [])].sort((a, b) => b.playCount - a.playCount).slice(0, 5);

    return (
      <Stack spacing={2}>
        {/* Hero metric row */}
        <Stack direction="row" spacing={2}>
          <Paper sx={{ p: 2, flex: 1, textAlign: "center" }}>
            <Typography variant="h3" fontWeight="bold">{stats.totalPlays}</Typography>
            <Typography variant="body2" color="text.secondary">{t("total_plays", { count: stats.totalPlays }).replace(`${stats.totalPlays} `, "")}</Typography>
          </Paper>
          <Paper sx={{ p: 2, flex: 1, textAlign: "center" }}>
            <Typography variant="h3" fontWeight="bold">{stats.uniquePlayers}</Typography>
            <Typography variant="body2" color="text.secondary">{t("unique_players", { count: stats.uniquePlayers }).replace(`${stats.uniquePlayers} `, "")}</Typography>
          </Paper>
        </Stack>

        {/* Chart */}
        {chartData.length > 0 && (
          <Paper sx={{ p: 2 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="subtitle2" color="text.secondary">
                {t("daily_plays_chart_title")}
              </Typography>
              <ToggleButtonGroup
                size="small"
                value={statsDays}
                exclusive
                onChange={(_, v) => v !== null && setStatsDays(v)}
              >
                <ToggleButton value={7}>{t("stats_7d")}</ToggleButton>
                <ToggleButton value={30}>{t("stats_30d")}</ToggleButton>
              </ToggleButtonGroup>
            </Stack>
            <ResponsiveContainerAny width="100%" height={180}>
              <AreaChartAny data={chartData}>
                <CartesianGridAny strokeDasharray="3 3" />
                <XAxisAny dataKey="day" tick={{ fontSize: 10 }} />
                <YAxisAny allowDecimals={false} width={30} tick={{ fontSize: 10 }} />
                <TooltipAny />
                <AreaAny type="monotone" dataKey="count" stroke="#ec7777" fill="#ec777740" />
              </AreaChartAny>
            </ResponsiveContainerAny>
          </Paper>
        )}

        {/* Rank cards side by side */}
        <Stack direction="row" spacing={2}>
          <RankCard
            icon={<EmojiEventsIcon sx={{ color: "#ffc107" }} />}
            label={t("global_rank")}
            rank={stats.globalRank}
            total={stats.globalTotal}
            onClick={() => history.push("/leaderboard")}
          />
          {(() => {
            const lr = stats.languageRanks;
            const idx = langRankIndex % (lr.length || 1);
            const current = lr[idx];
            const flag = current
              ? Object.values(Languages).find((l) => l.id === current.language)?.name ?? current.language
              : null;
            return (
              <Paper
                sx={{ p: 2, textAlign: "center", minWidth: 120, flex: 1, cursor: "pointer" }}
                onClick={() => history.push(current ? `/leaderboard?language=${current.language}` : "/leaderboard")}
              >
                <Stack alignItems="center" spacing={0.5}>
                  <Stack direction="row" alignItems="center" spacing={0}>
                    {lr.length > 1 && (
                      <IconButton size="small" onClick={(e) => { e.stopPropagation(); setLangRankIndex((idx - 1 + lr.length) % lr.length); }}>
                        <ChevronLeftIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    )}
                    {flag
                      ? <Typography sx={{ fontSize: 28, lineHeight: 1 }}>{flag}</Typography>
                      : <TranslateIcon color="primary" />
                    }
                    {lr.length > 1 && (
                      <IconButton size="small" onClick={(e) => { e.stopPropagation(); setLangRankIndex((idx + 1) % lr.length); }}>
                        <ChevronRightIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    )}
                  </Stack>
                  <Typography variant="caption" color="text.secondary">{t("language_rank")}</Typography>
                  <Typography variant="h5" fontWeight="bold">
                    {current && current.rank > 0 ? `#${current.rank}` : "\u2014"}
                  </Typography>
                  {current && current.total > 0 && (
                    <Typography variant="caption" color="text.secondary">
                      {t("out_of_creators", { rank: current.rank, total: current.total })}
                    </Typography>
                  )}
                </Stack>
              </Paper>
            );
          })()}
        </Stack>

        {/* Top performing decks table */}
        {topDecks.length > 0 && (
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              {t("profile_top_decks")}
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Deck</TableCell>
                    <TableCell align="right">Plays</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {topDecks.map((ds) => (
                    <TableRow key={ds.deckId}>
                      <TableCell>{ds.deckName || "Untitled"}</TableCell>
                      <TableCell align="right">
                        <Chip size="small" label={ds.playCount} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}
      </Stack>
    );
  };

  // ─── Decks View ──────────────────────────────────────────────────────────────
  const decksView = () => (
    <Stack spacing={2} sx={{ flex: 1, minHeight: 0 }}>
      {/* Search and controls */}
      {isMobile ? (
        <Stack spacing={1}>
          <TextField
            size="small"
            placeholder={t("profile_search_decks")}
            value={deckSearch}
            onChange={(e) => setDeckSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 20 }} />
                </InputAdornment>
              ),
            }}
            fullWidth
          />
          <Stack direction="row" spacing={2} alignItems="center">
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <Select
                value={deckSort}
                onChange={(e) => setDeckSort(e.target.value as DeckSort)}
              >
                <MenuItem value="recent">Recent</MenuItem>
                <MenuItem value="most_played">Most Played</MenuItem>
                <MenuItem value="alphabetical">A-Z</MenuItem>
              </Select>
            </FormControl>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreateNewDeck}
              sx={{ whiteSpace: "nowrap", ml: "auto !important" }}
            >
              Create
            </Button>
          </Stack>
        </Stack>
      ) : (
        <Stack direction="row" spacing={2} alignItems="center">
          <TextField
            size="small"
            placeholder={t("profile_search_decks")}
            value={deckSearch}
            onChange={(e) => setDeckSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 20 }} />
                </InputAdornment>
              ),
            }}
            sx={{ flex: 1 }}
          />
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <Select
              value={deckSort}
              onChange={(e) => setDeckSort(e.target.value as DeckSort)}
            >
              <MenuItem value="recent">Recent</MenuItem>
              <MenuItem value="most_played">Most Played</MenuItem>
              <MenuItem value="alphabetical">A-Z</MenuItem>
            </Select>
          </FormControl>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateNewDeck}
            sx={{ whiteSpace: "nowrap" }}
          >
            Create New Deck
          </Button>
        </Stack>
      )}

      {/* Deck grid */}
      <Paper
        sx={{
          flex: 1,
          overflowY: "auto",
          paddingInline: "1em",
          paddingBlock: "1em",
          minHeight: 0,
        }}
      >
        {filteredDecks.length === 0 ? (
          <Stack
            direction="column"
            justifyContent="center"
            alignItems="center"
            sx={{ height: "100%", width: "100%", animation: `${fadeIn} 2s`, py: 4 }}
          >
            {decks.length === 0 ? (
              <>
                <DoNotDisturbAltIcon sx={{ fontSize: { md: 100, xs: 80 } }} />
                <Typography sx={{ fontSize: 30 }}>No decks yet</Typography>
              </>
            ) : (
              <Typography color="text.secondary">No matching decks</Typography>
            )}
          </Stack>
        ) : (
          <Grid
            container
            wrap="wrap"
            justifyContent="flex-start"
            alignItems="flex-start"
            spacing={2}
            columns={{ xs: 2, sm: 3, md: 3, lg: 4 }}
            sx={{ pt: 1.5 }}
          >
            {filteredDecks.map((deck, index) => (
              <GridItem key={"deck-no" + index}>
                <CardDeck
                  featured={deck.featured}
                  title={deck.name}
                  questions={deck.questionCardCount}
                  answers={deck.answerCardCount}
                  categories={deck.categoriesCount}
                  playCount={deck.playCount}
                  language={deck.language}
                  id={deck.id}
                  options={[
                    {
                      name: "Delete",
                      function: () => handleDelete(deck.id),
                    },
                    {
                      name: "Edit",
                      function: () => {
                        history.push(`/deck/${deck.id}`);
                      },
                    },
                  ]}
                />
              </GridItem>
            ))}
          </Grid>
        )}
      </Paper>
    </Stack>
  );

  // ─── Settings View ──────────────────────────────────────────────────────────
  const settingsView = () => (
    <Stack spacing={3} sx={{ maxWidth: 500 }}>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Display Name</Typography>
        <TextField
          variant="outlined"
          fullWidth
          color={getNameFieldColor()}
          inputProps={{
            style: { fontSize: 24 },
            ref: inputEl,
            maxLength: 18,
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                {nameStatus === "checking" || nameStatus === "saving" ? (
                  <CircularProgress size={24} />
                ) : nameStatus === "saved" ? (
                  <CheckCircleOutlineIcon color="success" />
                ) : nameStatus === "taken" ? (
                  <ClearIcon
                    sx={{ cursor: "pointer", color: "error.main" }}
                    onClick={handleRevertName}
                  />
                ) : null}
              </InputAdornment>
            ),
          }}
          sx={{
            ...(nameStatus === "saved" && {
              "& .MuiOutlinedInput-notchedOutline": {
                animation: `${greenFlash} 1.5s ease-out`,
              },
            }),
          }}
          value={name}
          onChange={handleNameChange}
          helperText={nameStatus === "taken" ? t("name_taken") : nameStatus === "saved" ? t("name_saved") : undefined}
        />
      </Paper>

      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Bio</Typography>
        <TextField
          variant="outlined"
          fullWidth
          multiline
          rows={3}
          placeholder={t("profile_bio_placeholder")}
          value={bio}
          onChange={handleBioChange}
          inputProps={{ maxLength: 160 }}
          helperText={bioSaved ? t("name_saved") : `${bio.length}/160`}
        />
      </Paper>
    </Stack>
  );

  // ─── Content for active view ─────────────────────────────────────────────
  const renderContent = () => {
    switch (activeView) {
      case "dashboard":
        return dashboardView();
      case "decks":
        return decksView();
      case "settings":
        return settingsView();
    }
  };

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      <Box
        sx={{
          height: "100vh",
          display: "flex",
          flexDirection: "row",
          overflow: "hidden",
        }}
      >
        {/* Desktop sidebar */}
        {!isMobile && (
          <Paper
            sx={{
              width: SIDEBAR_WIDTH,
              minWidth: SIDEBAR_WIDTH,
              height: "100vh",
              borderRadius: 0,
              overflow: "auto",
            }}
          >
            {sidebarContent()}
          </Paper>
        )}

        {/* Mobile drawer */}
        {isMobile && (
          <Drawer
            open={drawerOpen}
            onClose={() => setDrawerOpen(false)}
            PaperProps={{ sx: { width: SIDEBAR_WIDTH } }}
          >
            {sidebarContent()}
          </Drawer>
        )}

        {/* Main content */}
        <Box
          sx={{
            flex: 1,
            overflow: "auto",
            p: { md: 3, xs: 2 },
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Mobile top bar */}
          {isMobile && (
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
              <IconButton onClick={() => setDrawerOpen(true)}>
                <MenuIcon />
              </IconButton>
              <Typography variant="h6" sx={{ flex: 1 }}>
                {navItems.find((n) => n.view === activeView)?.label}
              </Typography>
            </Stack>
          )}

          {renderContent()}
        </Box>
      </Box>

      {/* Deck picker dialog for share link */}
      <Dialog
        open={shareLinkDialogOpen}
        onClose={() => setShareLinkDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Select decks to share</DialogTitle>
        <DialogContent dividers>
          <List disablePadding>
            {decks.map((deck) => (
              <ListItemButton
                key={deck.id}
                dense
                onClick={() => {
                  setShareLinkSelectedIds((prev) =>
                    prev.includes(deck.id)
                      ? prev.filter((id) => id !== deck.id)
                      : [...prev, deck.id]
                  );
                }}
              >
                <Checkbox
                  edge="start"
                  checked={shareLinkSelectedIds.includes(deck.id)}
                  disableRipple
                />
                <ListItemText primary={deck.name || "Untitled"} />
              </ListItemButton>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShareLinkDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={shareLinkSelectedIds.length === 0}
            onClick={(e) => {
              setShareLinkDialogOpen(false);
              setConfigMenuAnchor(e.currentTarget as HTMLButtonElement);
            }}
          >
            Generate Link ({shareLinkSelectedIds.length})
          </Button>
        </DialogActions>
      </Dialog>

      {/* Config menu popover (after deck selection) */}
      <CreateDeckConfigMenu
        selectedDeckIds={shareLinkSelectedIds}
        anchorEl={configMenuAnchor}
        open={Boolean(configMenuAnchor)}
        onClose={() => setConfigMenuAnchor(null)}
        authUserName={name}
      />

      <CardWarning
        canEnable
        enableFeature={() => {
          stagedDeletion();
        }}
        setDontShowAgain={(dontShowAgain: boolean) =>
          setDontShowDeleteDeckWarningAgain(dontShowAgain)
        }
        warningTitle="Delete Deck"
        warningDescription="Do you really want to delete this deck?"
        showWarning={showDeleteDeckWarning}
        setShowWarning={(show: boolean) => setShowDeleteDeckWarning(show)}
        enableAnywayField="Delete"
      />
    </>
  );
}
